/**
 * useAudiencesData — Standalone hook for audience (population) data management.
 *
 * Handles:
 *  - Fetching audiences via populationsApi
 *  - Fetching company profiles via companyProfilesApi (search / hero; marketplace grid is org-grouped)
 *  - Socket invalidation via `onPopulationUpdated` while any audience is processing
 *  - Optimistic delete and toggle-marketplace mutations
 *  - Relative-time formatting for card footers
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { mutate as globalMutate } from 'swr'
import { populationsApi, companyProfilesApi } from '@/lib/api'
import type { UserProfile, CompanyProfile } from '@/lib/api'
import { toast as notify } from 'sonner'
import { buildAudiencePresentationSignature } from '../utils/audience-presentation'
import { onPopulationUpdated } from '@/lib/services/websocket'
import {
  MARKETPLACE_ORG_DOMAIN_PREFIX,
  MARKETPLACE_ORGANIZATION_UNKNOWN_TOKEN,
} from '@/lib/marketplace-audience-org-keys'
import { groupMarketplaceByIndustry } from '@/lib/utils/audience-industry-grouping'
import { canPublishAudienceToMarketplace } from '@/components/audiences/audience-sharing-permissions'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'

function marketplaceOrgDisplayName(audiences: any[]): string {
  for (const a of audiences) {
    const n = (a.organizationName as string | undefined)?.trim()
    if (n) return n
  }
  return 'Unknown organization'
}

function firstMarketplaceOrgLogoUrl(audiences: any[]): string | null {
  for (const a of audiences) {
    const u = (a.logoUrl as string | undefined)?.trim()
    if (u) return u
  }
  return null
}

export function useAudiencesData(profile: UserProfile | null, pagination?: { page: number; limit: number }) {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [audiences, setAudiences] = useState<any[]>([])
  const [audiencesLoading, setAudiencesLoading] = useState(false)
  const [audiencesLoaded, setAudiencesLoaded] = useState(false)
  const [totalAudiences, setTotalAudiences] = useState(0)
  const [deletingAudienceId, setDeletingAudienceId] = useState<string | null>(null)
  const [togglingAudienceGlobalId, setTogglingAudienceGlobalId] = useState<string | null>(null)
  const [companyProfiles, setCompanyProfiles] = useState<CompanyProfile[]>([])
  const [companyProfilesLoaded, setCompanyProfilesLoaded] = useState(false)

  const isPlatformAdmin = profile?.isPlatformAdmin === true
  const canPublishMarketplace = canPublishAudienceToMarketplace(profile)

  // ---------------------------------------------------------------------------
  // Load audiences
  // ---------------------------------------------------------------------------
  const prevSnapshotRef = useRef('')
  const inflightRef = useRef(false)
  const loadAudiences = useCallback(async (silent = false) => {
    if (inflightRef.current) return
    try {
      inflightRef.current = true
      if (!silent) setAudiencesLoading(true)
      let items: any[]
      let total: number
      if (pagination) {
        const result = await populationsApi.listPaginated(pagination)
        items = result.items
        total = result.total
      } else {
        items = await populationsApi.list({ limit: 200 })
        total = items.length
      }

      // Order-independent snapshot: sort by id so different API return orders
      // don't trigger spurious re-renders (which restart CSS transitions and
      // cause visible flickering on every card).
      const snapshot = [...items]
        .sort((a: any, b: any) => (a.id as string).localeCompare(b.id as string))
        .map((a: any) => buildAudiencePresentationSignature(a))
        .join('\n')
      if (silent && snapshot === prevSnapshotRef.current) return
      prevSnapshotRef.current = snapshot

      setAudiences(items)
      setTotalAudiences(total)
    } catch (error) {
      console.error('Failed to fetch audiences:', error)
    } finally {
      inflightRef.current = false
      setAudiencesLoaded(true)
      setAudiencesLoading(false)
    }
  }, [pagination])

  // Load company profiles (search / hero; marketplace sections are org-grouped)
  const loadCompanyProfiles = useCallback(async () => {
    try {
      const data = await companyProfilesApi.list()
      setCompanyProfiles(data)
    } catch (error) {
      console.error('Failed to fetch company profiles:', error)
    } finally {
      setCompanyProfilesLoaded(true)
    }
  }, [])

  // Initial load on mount
  useEffect(() => {
    if (!audiencesLoaded && !audiencesLoading) {
      loadAudiences()
    }
  }, [audiencesLoaded, audiencesLoading, loadAudiences])

  useEffect(() => {
    if (!companyProfilesLoaded) {
      loadCompanyProfiles()
    }
  }, [companyProfilesLoaded, loadCompanyProfiles])

  const loadAudiencesRef = useRef(loadAudiences)
  loadAudiencesRef.current = loadAudiences

  useEffect(() => {
    const unsubscribe = onPopulationUpdated(() => {
      loadAudiencesRef.current(true)
    })
    return () => {
      unsubscribe()
    }
  }, [])

  // Refetch when the tab becomes visible so new audiences appear after background
  // workflows complete (list polling may never start if no row had status processing).
  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadAudiencesRef.current(true)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const handleDeleteAudience = async (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    e.stopPropagation()

    try {
      setDeletingAudienceId(id)
      await populationsApi.delete(id)
      setAudiences(prev => prev.filter(a => a.id !== id))
    } catch (error) {
      console.error('Failed to delete audience:', error)
      notify.error('Failed to delete audience')
    } finally {
      setDeletingAudienceId(null)
    }
  }

  /** Optimistically remove an audience from the list (e.g. after leaving a shared audience). */
  const removeAudienceFromList = useCallback((id: string) => {
    setAudiences(prev => prev.filter(a => a.id !== id))
  }, [])

  const handleToggleAudienceGlobal = async (e: React.MouseEvent, id: string): Promise<{ ok: boolean; error?: string }> => {
    e.preventDefault()
    e.stopPropagation()

    try {
      setTogglingAudienceGlobalId(id)
      const audience = audiences.find(a => a.id === id)
      const updated = await populationsApi.toggleGlobal(id)
      const newMode = updated.sharingMode ?? (updated.isGlobal ? 'marketplace' : 'private')
      setAudiences(prev => prev.map(a =>
        a.id === id ? { ...a, sharingMode: newMode, publisherDomain: updated.publisherDomain, canEdit: true } : a
      ))
      loadCompanyProfiles()
      if (newMode === 'marketplace') {
        const name = audience?.name || 'Audience'
        notify.success(`Published! ${name} is now visible to all users.`)
      }
      return { ok: true }
    } catch (error: unknown) {
      console.error('Failed to toggle global status:', error)
      const message = blockedFeatureMessage(error)
        || (error instanceof Error ? error.message : null)
        || ((error as any)?.response?.data?.message)
        || 'Failed to update audience visibility'
      notify.error(message)
      return { ok: false, error: message }
    } finally {
      setTogglingAudienceGlobalId(null)
    }
  }

  const handleBookmarkToggle = useCallback(async (id: string, nextBookmarked: boolean) => {
    // Optimistic update
    setAudiences(prev => prev.map(a =>
      a.id === id
        ? { ...a, isBookmarked: nextBookmarked, bookmarkedAt: nextBookmarked ? new Date().toISOString() : null }
        : a,
    ))
    try {
      if (nextBookmarked) {
        await populationsApi.bookmark(id)
      } else {
        await populationsApi.unbookmark(id)
      }
      await globalMutate('home-audiences')
    } catch (error) {
      console.error('Failed to toggle bookmark:', error)
      // Revert on failure
      setAudiences(prev => prev.map(a =>
        a.id === id ? { ...a, isBookmarked: !nextBookmarked, bookmarkedAt: !nextBookmarked ? new Date().toISOString() : null } : a,
      ))
      notify.error('Failed to update bookmark')
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Derived: group public audiences by creator organization (stable token)
  // ---------------------------------------------------------------------------
  const publicAudiencesByProvider = useMemo(() => {
    const publicAudiences = audiences.filter((a: any) => a.sharingMode === 'marketplace')

    const byOrg = new Map<string, any[]>()
    for (const a of publicAudiences) {
      const token =
        (a.organizationToken as string | undefined)?.trim() || MARKETPLACE_ORGANIZATION_UNKNOWN_TOKEN
      const list = byOrg.get(token) ?? []
      list.push(a)
      byOrg.set(token, list)
    }

    const orgEntries = Array.from(byOrg.entries()).sort((left, right) => {
      const nameLeft = marketplaceOrgDisplayName(left[1])
      const nameRight = marketplaceOrgDisplayName(right[1])
      const cmp = nameLeft.localeCompare(nameRight, undefined, { sensitivity: 'base' })
      if (cmp !== 0) return cmp
      return left[0].localeCompare(right[0])
    })

    return orgEntries.map(([orgToken, list]) => {
      const domainKey = `${MARKETPLACE_ORG_DOMAIN_PREFIX}${orgToken}`
      const displayName = marketplaceOrgDisplayName(list)
      const logoUrl = firstMarketplaceOrgLogoUrl(list)
      return {
        provider: {
          domain: domainKey,
          name: displayName,
          logoUrl,
          description: null,
          badge: null,
          brandColors: null,
          fetchedAt: '',
          audienceCount: list.length,
        } satisfies CompanyProfile,
        audiences: list,
      }
    })
  }, [audiences])

  // ---------------------------------------------------------------------------
  // Derived: group marketplace audiences by tier-1 industry
  // ---------------------------------------------------------------------------
  const publicAudiencesByIndustry = useMemo(
    () => groupMarketplaceByIndustry(audiences),
    [audiences],
  )

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    if (diffDays < 14) return '1 week ago'
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return date.toLocaleDateString()
  }

  return {
    audiences,
    audiencesLoading,
    totalAudiences,
    deletingAudienceId,
    togglingAudienceGlobalId,
    isPlatformAdmin,
    canPublishMarketplace,
    companyProfiles,
    publicAudiencesByProvider,
    publicAudiencesByIndustry,
    handleDeleteAudience,
    removeAudienceFromList,
    refreshAudiences: () => loadAudiences(true),
    handleToggleAudienceGlobal,
    handleBookmarkToggle,
    formatRelativeTime,
  }
}
